// Resume43.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Divider,
  IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, DragIndicator, Reorder,
  Phone as PhoneIcon, Email as EmailIcon, LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon, Language as LanguageIcon, DriveEta as DriveEtaIcon,
} from '@mui/icons-material';

export const resumeMeta = { hasPhoto: false, columns: 1 };

const pageSizeMap = {
  A4: { width: '210mm', minHeight: '297mm', height: '297mm' },
  Letter: { width: '216mm', minHeight: '279mm', height: '279mm' },
  Legal: { width: '216mm', minHeight: '356mm', height: '356mm' },
  A3: { width: '297mm', minHeight: '420mm', height: '420mm' },
  Executive: { width: '184mm', minHeight: '267mm', height: '267mm' },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultSectionOrder = [
  'about', 'experience', 'education', 'skills', 'languages',
  'certifications', 'accomplishments', 'additionalInfo', 'software',
  'volunteering', 'interests', 'websites', 'references',
];

const Resume43 = ({
  color = '#6a1b9a',
  font = "Georgia, 'Times New Roman', serif",
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '24px',
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  lineWeight = 1,
  pageSize = 'A4',
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const primaryColor = color;
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  // Spacing helpers
  const itemSpacing = `${Math.round(paragraphSpacing / 3.5)}px`;
  const sectionDividerSpacing = `${Math.round(sectionSpacing / 10)}px`;

  const safeJSON = (str, fallback = null) => {
    try { return JSON.parse(str) || fallback; } catch { return fallback; }
  };

  const ensureArray = (val) => (Array.isArray(val) ? val : []);

  // ── Pagination states ──────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBreaks, setPageBreaks] = useState([]);
  const contentRef = useRef(null);

  const [personalData, setPersonalData] = useState({
    firstName: 'Jacqueline', lastName: 'Thompson', currentPosition: '',
    phone: '', email: '', city: '', website: '', linkedIn: '', drivingLicence: ''
  });
  const [savedSummary, setSavedSummary] = useState('');
  const [savedSkills, setSavedSkills] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const formatDate = (month, year) => [month, year].filter(Boolean).join(' ');

  useEffect(() => {
    const stored = safeJSON(localStorage.getItem('personalData'), {});
    setPersonalData(prev => ({ ...prev, ...stored, ...formData }));

    const summaries = safeJSON(localStorage.getItem('summaries'), []);
    if (summaries.length > 0) setSavedSummary(summaries[summaries.length - 1]);

    const edu = localStorage.getItem('educationEntries');
    if (edu) setEducationEntries(safeJSON(edu, []));

    const sk = localStorage.getItem('skills');
    if (sk) setSavedSkills(safeJSON(sk, []));

    const work = localStorage.getItem('workExperiences');
    if (work) setWorkExperiences(safeJSON(work, []));

    const lang = localStorage.getItem('languagesList');
    if (lang) setSavedLanguages(safeJSON(lang, []));

    const cert = localStorage.getItem('certificationsList');
    if (cert) setSavedCertifications(safeJSON(cert, []));

    const acc = localStorage.getItem('accomplishmentsList');
    if (acc) setSavedAccomplishments(safeJSON(acc, []));

    const add = localStorage.getItem('additionalInfo');
    if (add) setSavedAdditionalInfo(safeJSON(add, []));

    const soft = localStorage.getItem('softwareSkills');
    if (soft) setSoftwareSkills(safeJSON(soft, []));

    const ref = localStorage.getItem('referencesList');
    if (ref) setSavedReferences(safeJSON(ref, []));

    const vol = localStorage.getItem('volunteering');
    if (vol) setSavedVolunteering(safeJSON(vol, []));

    const int = localStorage.getItem('interests');
    if (int) setSavedInterests(safeJSON(int, []));

    const web = localStorage.getItem('websites');
    if (web) setSavedWebsites(safeJSON(web, []));

    const userId = localStorage.getItem('userId');
    let sectionsData = {};
    if (userId) {
      const list = safeJSON(localStorage.getItem(`current_custom_sections_${userId}`), []);
      if (Array.isArray(list)) {
        list.forEach(name => {
          const parsed = safeJSON(localStorage.getItem(`custom_${userId}_${name}`), {});
          const items = (parsed.items || []).filter(v => v && v.trim());
          if (items.length) sectionsData[name] = items;
        });
      }
    }
    setCustomSections(sectionsData);

    const savedOrder = safeJSON(localStorage.getItem('resume43SectionOrder'), null);
    if (savedOrder) {
      const base = savedOrder.filter(s => !s.startsWith('custom_'));
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setSectionOrder([...base, ...customIds]);
    } else {
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setSectionOrder([...defaultSectionOrder, ...customIds]);
    }
  }, [formData]);

  useEffect(() => {
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    setSectionOrder(prev => {
      const base = prev.filter(s => !s.startsWith('custom_'));
      const next = [...base, ...customIds];
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem('resume43SectionOrder', JSON.stringify(next));
        return next;
      }
      return prev;
    });
  }, [customSections]);

  useEffect(() => {
    if (sectionOrder.length > 0) localStorage.setItem('resume43SectionOrder', JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  const handleDragStart = (e, index) => { e.dataTransfer.setData('text/plain', index); e.currentTarget.style.opacity = '0.4'; };
  const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDrop = (e, index) => {
    e.preventDefault();
    const src = Number(e.dataTransfer.getData('text/plain'));
    if (src !== index) {
      const next = [...sectionOrder];
      const [moved] = next.splice(src, 1);
      next.splice(index, 0, moved);
      setSectionOrder(next);
    }
    setDragOverIndex(null);
  };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = '1'; setDragOverIndex(null); };
  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);

  const getSkillName = s => typeof s === 'string' ? s : (s.name || s.category || s.skill || '');
  const getLangText = l => {
    if (typeof l === 'string') return l;
    const n = l.language || l.name || '';
    const lv = l.level || l.proficiency || '';
    return lv ? `${n} (${lv})` : n;
  };

  const SectionHeading = ({ children }) => (
    <Typography sx={{ fontWeight: 700, color: primaryColor, mb: itemSpacing, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: font, fontSize: headingSize }}>
      {children}
    </Typography>
  );

  const renderSection = (id) => {
    switch (id) {
      case 'about':
        if (!savedSummary) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Summary</SectionHeading>
            <Typography sx={{ lineHeight: `${lineSpacing / 10}`, fontSize, fontFamily: font, fontStyle, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{savedSummary}</Typography>
          </Box>
        );

      case 'experience':
        if (!workExperiences.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Work Experience</SectionHeading>
            {workExperiences.map((w, i) => (
              <Box key={i} mb={itemSpacing}>
                <Typography sx={{ fontWeight: 700, fontFamily: font, fontSize }}>{w.jobTitle || w.title || 'Position'}{w.employer || w.company ? `, ${w.employer || w.company}` : ''}</Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', fontFamily: font, fontSize }}>
                  {formatDate(w.startMonth, w.startYear)}{(w.startMonth || w.startYear) ? ' – ' : ''}{w.current ? 'Present' : formatDate(w.endMonth, w.endYear)}
                </Typography>
                {w.description && (
                  <Box component="ul" sx={{ pl: 3, mt: 0.5, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}
                    dangerouslySetInnerHTML={{ __html: (w.description || '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>').split('\n').filter(l => l.trim()).map(l => `<li>${l.trim().replace(/^[•\-]\s*/, '')}</li>`).join('') }} />
                )}
              </Box>
            ))}
          </Box>
        );

      case 'education':
        if (!educationEntries.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Education</SectionHeading>
            {educationEntries.map((edu, i) => {
              const isSchool = edu.educationLevel === 'school';
              const inst = isSchool ? edu.schoolName : (edu.collegeName || edu.schoolName || 'Institution');
              const deg = isSchool ? `${edu.standard || ''} Standard` : (edu.degreeName || edu.degree || '');
              const spec = isSchool ? (edu.stream || '') : (edu.specialization || '');
              const grad = formatDate(edu.gradMonth, edu.gradYear);
              const extra = [edu.cgpa && `CGPA: ${edu.cgpa}`, edu.percentage && `${edu.percentage}%`].filter(Boolean).join(' • ');
              return (
                <Box key={i} mb={itemSpacing}>
                  <Typography sx={{ fontWeight: 700, fontFamily: font, fontSize }}>{deg}{spec ? ` in ${spec}` : ''}</Typography>
                  <Typography sx={{ color: '#555', fontFamily: font, fontSize }}>{inst}{grad ? ` • ${grad}` : ''}{extra ? ` • ${extra}` : ''}</Typography>
                </Box>
              );
            })}
          </Box>
        );

      case 'skills':
        if (!savedSkills.length && !softwareSkills.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Skills</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedSkills.map((s, i) => <li key={i}>{getSkillName(s)}</li>)}
              {softwareSkills.map((s, i) => { const n = typeof s === 'string' ? s : (s.name || ''); return <li key={`sw-${i}`}>{n}</li>; })}
            </Box>
          </Box>
        );

      case 'languages':
        if (!savedLanguages.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Languages</SectionHeading>
            <Typography sx={{ fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedLanguages.map((l, i) => <span key={i}>{getLangText(l)}{i < savedLanguages.length - 1 ? ', ' : ''}</span>)}
            </Typography>
          </Box>
        );

      case 'certifications':
        if (!savedCertifications.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Certifications</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedCertifications.map((c, i) => {
                const name = c.name || c.certName || c.title || '';
                const provider = c.provider || c.organization || '';
                const year = c.year || c.date || '';
                return <li key={i}>{name}{provider ? ` – ${provider}` : ''}{year ? ` (${year})` : ''}</li>;
              })}
            </Box>
          </Box>
        );

      case 'accomplishments':
        if (!savedAccomplishments.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Accomplishments</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedAccomplishments.map((a, i) => { const t = typeof a === 'string' ? a : (a.title || a.description || a.name || ''); return <li key={i}>{t}</li>; })}
            </Box>
          </Box>
        );

      case 'additionalInfo':
        if (!savedAdditionalInfo.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Additional Information</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedAdditionalInfo.map((a, i) => { const t = typeof a === 'string' ? a : (a.info || a.text || a.description || ''); return <li key={i}>{t}</li>; })}
            </Box>
          </Box>
        );

      case 'software':
        if (!softwareSkills.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Software Skills</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {softwareSkills.map((s, i) => { const n = typeof s === 'string' ? s : (s.name || ''); const lv = typeof s === 'object' ? (s.level || s.rating || '') : ''; return <li key={i}>{n}{lv ? ` - ${lv}%` : ''}</li>; })}
            </Box>
          </Box>
        );

      case 'volunteering':
        if (!savedVolunteering.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Volunteering</SectionHeading>
            {savedVolunteering.map((v, i) => {
              const role = typeof v === 'string' ? v : (v.subtopic || v.role || v.title || '');
              const org = typeof v === 'object' ? (v.organization || v.org || '') : '';
              const from = typeof v === 'object' ? (v.fromDate || v.startDate || '') : '';
              const to = typeof v === 'object' ? (v.toDate || v.endDate || '') : '';
              const desc = typeof v === 'object' ? (v.content || v.description || '') : '';
              return (
                <Box key={i} mb={itemSpacing}>
                  <Typography sx={{ fontWeight: 700, fontFamily: font, fontSize }}>{role}{org ? ` — ${org}` : ''}</Typography>
                  {(from || to) && <Typography sx={{ color: '#555', fontStyle: 'italic', fontFamily: font, fontSize }}>{from}{from && to ? ' to ' : ''}{to}</Typography>}
                  {desc && <Typography sx={{ mt: 0.5, fontFamily: font, fontSize, lineHeight: `${lineSpacing / 10}` }}>{desc}</Typography>}
                </Box>
              );
            })}
          </Box>
        );

      case 'interests':
        if (!savedInterests.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Interests</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedInterests.map((item, i) => { const t = typeof item === 'string' ? item : (item.interest || item.name || ''); return <li key={i}>{t}</li>; })}
            </Box>
          </Box>
        );

      case 'websites':
        if (!savedWebsites.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>Websites & Portfolios</SectionHeading>
            <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
              {savedWebsites.map((site, i) => {
                const url = typeof site === 'string' ? site : (site.url || site.link || '');
                const name = typeof site === 'object' ? (site.name || site.label || url) : url;
                return <li key={i}><a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" style={{ color: primaryColor, textDecoration: 'none', fontFamily: font }}>{name}</a></li>;
              })}
            </Box>
          </Box>
        );

      case 'references':
        if (!savedReferences.length) return null;
        return (
          <Box className="resume43-section" mb={sectionDividerSpacing}>
            <SectionHeading>References</SectionHeading>
            {savedReferences.map((ref, i) => {
              if (typeof ref === 'string') return <Typography key={i} sx={{ fontFamily: font, fontSize, mb: itemSpacing, whiteSpace: 'pre-line' }}>{ref}</Typography>;
              const name = ref.refName || ref.name || ref.referenceName || '';
              const company = ref.company || ref.companyName || '';
              const pos = ref.position || ref.jobTitle || '';
              return (
                <Box key={i} mb={itemSpacing}>
                  {name && <Typography sx={{ fontWeight: 700, fontFamily: font, fontSize }}>{name}</Typography>}
                  {(company || pos) && <Typography sx={{ color: '#555', fontFamily: font, fontSize }}>{company}{pos ? ` / ${pos}` : ''}</Typography>}
                  {ref.phone && <Typography sx={{ fontFamily: font, fontSize }}>Phone: {ref.phone}</Typography>}
                  {ref.email && <Typography sx={{ fontFamily: font, fontSize }}>Email: {ref.email}</Typography>}
                </Box>
              );
            })}
          </Box>
        );

      default:
        if (id && id.startsWith('custom_')) {
          const name = id.replace('custom_', '');
          const items = customSections[name] || [];
          if (!items.length) return null;
          return (
            <Box className="resume43-section" mb={sectionDividerSpacing}>
              <SectionHeading>{name.replace(/_/g, ' ')}</SectionHeading>
              <Box component="ul" sx={{ pl: 3, fontSize, fontFamily: font, lineHeight: `${lineSpacing / 10}` }}>
                {items.map((item, i) => <li key={i}>{item}</li>)}
              </Box>
            </Box>
          );
        }
        return null;
    }
  };

  const data = { ...personalData, ...formData };

  const ResumeHeader = () => (
    <Box className="resume-header-area" sx={{ textAlign: 'center', mb: 3 }}>
      <Typography sx={{ fontWeight: 900, letterSpacing: '2px', color: primaryColor, fontSize: '2.6rem', lineHeight: 1.1, mb: 1, fontFamily: font }}>
        {(data.firstName || 'FIRST').toUpperCase()} {(data.lastName || 'LAST').toUpperCase()}
      </Typography>
      {(data.currentPosition) && (
        <Typography sx={{ fontWeight: 700, color: primaryColor, letterSpacing: '1.5px', fontSize: '1.2rem', textTransform: 'uppercase', mb: 2, fontFamily: font }}>
          {data.currentPosition}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
        {data.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PhoneIcon sx={{ fontSize: 15, color: '#555' }} /><Typography sx={{ fontSize: '0.9rem', color: '#444', fontFamily: font }}>{data.phone}</Typography></Box>}
        {data.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EmailIcon sx={{ fontSize: 15, color: '#555' }} /><Typography sx={{ fontSize: '0.9rem', color: '#444', fontFamily: font, wordBreak: 'break-all' }}>{data.email}</Typography></Box>}
        {data.city && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOnIcon sx={{ fontSize: 15, color: '#555' }} /><Typography sx={{ fontSize: '0.9rem', color: '#444', fontFamily: font }}>{data.city}</Typography></Box>}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        {data.linkedIn && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LinkedInIcon sx={{ fontSize: 15, color: '#0077b5' }} /><Typography sx={{ fontSize: '0.9rem', color: '#0077b5', fontFamily: font, wordBreak: 'break-all' }}>{data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}</Typography></Box>}
        {data.website && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LanguageIcon sx={{ fontSize: 15, color: '#1976d2' }} /><Typography sx={{ fontSize: '0.9rem', color: '#1976d2', fontFamily: font, wordBreak: 'break-all' }}>{data.website.replace(/^https?:\/\/(www\.)?/, '')}</Typography></Box>}
        {data.drivingLicence && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><DriveEtaIcon sx={{ fontSize: 15, color: '#555' }} /><Typography sx={{ fontSize: '0.9rem', color: '#555', fontFamily: font }}>DL: {data.drivingLicence}</Typography></Box>}
      </Box>
    </Box>
  );

  // ── Calculate which sections belong to each page ──────────────────────────
  const getPageSections = (pageIdx) => {
    if (totalPages === 1 || pageBreaks.length === 0) return sectionOrder;

    const container = contentRef.current;
    if (!container) return sectionOrder;

    const sections = container.querySelectorAll(".resume43-section");
    const pageHeight = pagePx.height - (topBottomMargin) * 2;
    
    const headerEl = container.querySelector(".resume-header-area");
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 150;

    let accumulated = 0;
    let currentIdx = 0;
    const pageChunks = [[]];

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      const currentLimit = pageHeight * (currentIdx + 1) - (currentIdx === 0 ? headerHeight : 0);
      
      if (accumulated + h > currentLimit) {
        currentIdx++;
        pageChunks.push([]);
      }
      if (sectionOrder[i]) {
        pageChunks[currentIdx].push(sectionOrder[i]);
      }
      accumulated += h;
    });

    return pageChunks[pageIdx] || [];
  };

  const getSectionsForCurrentPage = () => {
    return getPageSections(currentPage);
  };

  // ── Trigger pagination after content renders ──────────────────────────────
  useEffect(() => {
    if (exporting) return;
    const container = contentRef.current;
    if (!container) return;

    const headerEl = container.querySelector(".resume-header-area");
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 150;

    const pageHeight = pagePx.height - (topBottomMargin) * 2;
    const sections = container.querySelectorAll(".resume43-section");
    const breaks = [];
    let accumulated = 0;
    let pageIndex = 1;

    sections.forEach((section) => {
      const h = section.getBoundingClientRect().height;
      const currentLimit = pageHeight * pageIndex - (pageIndex === 1 ? headerHeight : 0);
      
      if (accumulated + h > currentLimit) {
        breaks.push(pageIndex);
        pageIndex++;
      }
      accumulated += h;
    });

    const total = Math.max(1, pageIndex);
    setTotalPages(total);
    setPageBreaks(breaks);
  }, [
    sectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, pagePx.height, exporting,
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative', fontFamily: font }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2 }}>
          <IconButton
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="outlined" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)} size="small" sx={{ color: primaryColor, borderColor: primaryColor }}>Arrange Sections</Button>
              <Button variant="outlined" color="error" onClick={resetSectionOrder} size="small">Reset All</Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container */}
      <Box
        ref={contentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: page.width,
          p: `${topBottomMargin}px ${sideMargins}px`,
          boxSizing: "border-box",
        }}
      >
        <ResumeHeader />
        <Divider sx={{ borderColor: primaryColor, borderWidth: lineWeight, my: 2 }} />
        {sectionOrder.map((id, index) => {
          const content = renderSection(id);
          if (!content) return null;
          return (
            <React.Fragment key={id}>
              {content}
              {index < sectionOrder.length - 1 && <Divider sx={{ borderColor: '#ddd', my: sectionDividerSpacing }} />}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Pages Container */}
      <Box id="resume-container">
        {exporting ? (
          // Render all pages sequentially for PDF download
          Array.from({ length: totalPages }).map((_, pageIdx) => (
            <Paper
              key={pageIdx}
              elevation={0}
              className="resume-page"
              sx={{
                width: page.width,
                height: page.height,
                bgcolor: 'white',
                mx: 'auto',
                mb: '24px',
                p: `${topBottomMargin}px ${sideMargins}px`,
                fontFamily: font,
                color: '#1a1a1a',
                boxSizing: 'border-box',
                overflow: 'hidden',
                pageBreakAfter: 'always',
                '@media print': {
                  margin: 0,
                  boxShadow: 'none',
                  pageBreakAfter: 'always',
                }
              }}
            >
              {pageIdx === 0 && <ResumeHeader />}
              {pageIdx === 0 && <Divider sx={{ borderColor: primaryColor, borderWidth: lineWeight, my: 2 }} />}
              {getPageSections(pageIdx).map((id, index) => {
                const content = renderSection(id);
                if (!content) return null;
                return (
                  <React.Fragment key={id}>
                    {content}
                    {index < getPageSections(pageIdx).length - 1 && (
                      <Divider sx={{ borderColor: '#ddd', my: sectionDividerSpacing }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Paper>
          ))
        ) : (
          // Render only current page in the editor UI
          <Paper
            elevation={3}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              bgcolor: 'white',
              mx: 'auto',
              p: `${topBottomMargin}px ${sideMargins}px`,
              fontFamily: font,
              color: '#1a1a1a',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {currentPage === 0 && <ResumeHeader />}
            {currentPage === 0 && <Divider sx={{ borderColor: primaryColor, borderWidth: lineWeight, my: 2 }} />}
            {getSectionsForCurrentPage().map((id, index) => {
              const content = renderSection(id);
              if (!content) return null;
              return (
                <React.Fragment key={id}>
                  {content}
                  {index < getSectionsForCurrentPage().length - 1 && (
                    <Divider sx={{ borderColor: '#ddd', my: sectionDividerSpacing }} />
                  )}
                </React.Fragment>
              );
            })}
          </Paper>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Arrange Resume Sections</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {sectionOrder.map((id, index) => (
              <Paper key={id} draggable
                onDragStart={e => handleDragStart(e, index)} onDragOver={e => handleDragOver(e, index)}
                onDrop={e => handleDrop(e, index)} onDragEnd={handleDragEnd}
                sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', bgcolor: dragOverIndex === index ? '#f0f8ff' : 'white', border: dragOverIndex === index ? `2px dashed ${primaryColor}` : '1px solid #ddd', cursor: 'grab', userSelect: 'none' }}>
                <DragIndicator sx={{ mr: 2, color: '#999' }} />
                <Typography sx={{ flexGrow: 1, textTransform: 'capitalize', fontSize: '13px' }}>
                  {id.replace(/-/g, ' ').replace('custom_', '').replace(/_/g, ' ')}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume43;